class CTSnohettaCarousel extends HTMLElement {
  constructor() {
    super();

    this.position = 0;
    this.activeIndex = 0;

    this.autoplayTimer = null;
    this.animationFrame = null;
    this.resizeTimer = null;

    this.isHovering = false;
    this.isFocused = false;
    this.isDragging = false;
    this.hasDragged = false;

    this.pointerStartX = 0;
    this.pointerCurrentX = 0;
    this.dragStartPosition = 0;

    this.slideLayouts = [];

    this.dragClickThreshold = 6;
    this.swipeThreshold = 50;
    this.animationDuration = 650;
  }

  connectedCallback() {
    this.visual = this.querySelector('[data-carousel-visual]');
    this.slideWrapper = this.querySelector('[data-slide-wrapper]');
    this.dragWrapper = this.querySelector('[data-drag-wrapper]');
    this.textWrapper = this.querySelector('[data-text-wrapper]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.slideButtons = Array.from(this.querySelectorAll('[data-slide-button]'));
    this.textPanels = Array.from(this.querySelectorAll('[data-text-panel]'));

    if (!this.visual || !this.slideWrapper || !this.dragWrapper || this.slides.length === 0) {
      return;
    }

    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplayInterval = Number(this.dataset.interval) || 5000;
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.bindEvents();
    this.render();
    this.setActiveIndex(0, true);
    this.updateTextWrapperHeight();
    this.startAutoplay();

    window.requestAnimationFrame(() => {
      this.updateTextWrapperHeight();
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.updateTextWrapperHeight();
      });
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.cancelAnimation();

    window.removeEventListener('resize', this.handleResize);

    this.removeEventListener('mouseenter', this.handleMouseEnter);
    this.removeEventListener('mouseleave', this.handleMouseLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);

    if (this.dragWrapper) {
      this.dragWrapper.removeEventListener('pointerdown', this.handlePointerDown);
      this.dragWrapper.removeEventListener('pointermove', this.handlePointerMove);
      this.dragWrapper.removeEventListener('pointerup', this.handlePointerUp);
      this.dragWrapper.removeEventListener('pointercancel', this.handlePointerCancel);
      this.dragWrapper.removeEventListener('lostpointercapture', this.handlePointerCancel);
    }
  }

  bindEvents() {
    this.handleResize = this.onResize.bind(this);
    this.handleMouseEnter = this.onMouseEnter.bind(this);
    this.handleMouseLeave = this.onMouseLeave.bind(this);
    this.handleFocusIn = this.onFocusIn.bind(this);
    this.handleFocusOut = this.onFocusOut.bind(this);
    this.handlePointerDown = this.onPointerDown.bind(this);
    this.handlePointerMove = this.onPointerMove.bind(this);
    this.handlePointerUp = this.onPointerUp.bind(this);
    this.handlePointerCancel = this.onPointerCancel.bind(this);

    window.addEventListener('resize', this.handleResize);

    this.addEventListener('mouseenter', this.handleMouseEnter);
    this.addEventListener('mouseleave', this.handleMouseLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);

    this.dragWrapper.addEventListener('pointerdown', this.handlePointerDown);
    this.dragWrapper.addEventListener('pointermove', this.handlePointerMove);
    this.dragWrapper.addEventListener('pointerup', this.handlePointerUp);
    this.dragWrapper.addEventListener('pointercancel', this.handlePointerCancel);
    this.dragWrapper.addEventListener('lostpointercapture', this.handlePointerCancel);

    this.slideButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        if (this.hasDragged) {
          return;
        }

        this.goToSlide(index);
        this.restartAutoplay();
      });
    });
  }

  onResize() {
    window.clearTimeout(this.resizeTimer);

    this.resizeTimer = window.setTimeout(() => {
      this.render();
      this.updateTextWrapperHeight();
    }, 120);
  }

  onMouseEnter() {
    this.isHovering = true;
  }

  onMouseLeave() {
    this.isHovering = false;
  }

  onFocusIn() {
    this.isFocused = true;
  }

  onFocusOut() {
    this.isFocused = false;
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.cancelAnimation();

    if (event.cancelable) {
      event.preventDefault();
    }

    this.isDragging = true;
    this.hasDragged = false;
    this.pointerStartX = event.clientX;
    this.pointerCurrentX = event.clientX;
    this.dragStartPosition = this.position;

    this.classList.add('is-dragging');
    this.dragWrapper.classList.add('is-dragging');

    if (this.dragWrapper.setPointerCapture) {
      this.dragWrapper.setPointerCapture(event.pointerId);
    }
  }

  onPointerMove(event) {
    if (!this.isDragging) {
      return;
    }

    this.pointerCurrentX = event.clientX;

    const dragDistance = this.pointerCurrentX - this.pointerStartX;

    if (Math.abs(dragDistance) > this.dragClickThreshold) {
      this.hasDragged = true;

      if (event.cancelable) {
        event.preventDefault();
      }
    }

    const config = this.getLayoutConfig();
    const stepPixels = Math.max(this.dragWrapper.clientWidth * (config.active / 100), 1);

    this.position = this.dragStartPosition - dragDistance / stepPixels;
    this.render();
  }

  onPointerUp(event) {
    if (!this.isDragging) {
      return;
    }

    const dragDistance = this.pointerCurrentX - this.pointerStartX;

    this.isDragging = false;
    this.classList.remove('is-dragging');
    this.dragWrapper.classList.remove('is-dragging');

    if (this.dragWrapper.hasPointerCapture && this.dragWrapper.hasPointerCapture(event.pointerId)) {
      this.dragWrapper.releasePointerCapture(event.pointerId);
    }

    if (this.hasDragged) {
      this.snapAfterDrag(dragDistance);
      this.restartAutoplay();

      window.setTimeout(() => {
        this.hasDragged = false;
      }, 80);

      return;
    }

    const clickedIndex = this.getSlideIndexFromPoint(event.clientX);

    if (clickedIndex !== null && clickedIndex !== this.activeIndex) {
      this.goToSlide(clickedIndex);
      this.restartAutoplay();
    }
  }

  onPointerCancel() {
    if (!this.isDragging) {
      return;
    }

    this.isDragging = false;
    this.classList.remove('is-dragging');
    this.dragWrapper.classList.remove('is-dragging');

    this.animateTo(Math.round(this.position));
  }

  getLayoutConfig() {
    const isDesktop = window.matchMedia('(min-width: 990px)').matches;

    if (isDesktop) {
      return {
        active: 36,
        near: 24,
        far: 16
      };
    }

    return {
      active: 78,
      near: 34,
      far: 18
    };
  }

  getLayoutForRelative(relative, config) {
    const active = config.active;
    const near = config.near;
    const far = config.far;
    const activeLeft = (100 - active) / 2;

    let left;
    let width;

    if (relative >= 0) {
      if (relative <= 1) {
        left = activeLeft + active * relative;
        width = this.lerp(active, near, relative);
      } else if (relative <= 2) {
        left = activeLeft + active + near * (relative - 1);
        width = this.lerp(near, far, relative - 1);
      } else {
        left = activeLeft + active + near + far * (relative - 2);
        width = far;
      }
    } else {
      const distance = Math.abs(relative);

      if (distance <= 1) {
        left = activeLeft - near * distance;
        width = this.lerp(active, near, distance);
      } else if (distance <= 2) {
        left = activeLeft - near - far * (distance - 1);
        width = this.lerp(near, far, distance - 1);
      } else {
        left = activeLeft - near - far - far * (distance - 2);
        width = far;
      }
    }

    return {
      left,
      width
    };
  }

  getSlideVisualState(relative) {
    const absRelative = Math.abs(relative);

    const activeImageRatio = 66.6666666667;
    const mobileSecondaryImageRatio = 92;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;

    const ratioProgress = Math.min(absRelative, 1);
    const imageRatio = isMobile
      ? this.lerp(activeImageRatio, mobileSecondaryImageRatio, ratioProgress)
      : activeImageRatio;

    let imageBlur;

    if (absRelative <= 1) {
      imageBlur = this.lerp(0, 1.2, absRelative);
    } else {
      imageBlur = this.lerp(1.2, 2.4, Math.min(absRelative - 1, 1));
    }

    let imageScale;

    if (absRelative <= 1) {
      imageScale = this.lerp(1, 1.035, absRelative);
    } else {
      imageScale = this.lerp(1.035, 1.07, Math.min(absRelative - 1, 1));
    }

    const imageBrightness = this.lerp(1, 0.98, Math.min(absRelative, 1));
    const imageSaturation = this.lerp(1, 0.95, Math.min(absRelative, 1));

    return {
      absRelative,
      imageRatio,
      imageBlur,
      imageScale,
      imageBrightness,
      imageSaturation
    };
  }

  render() {
    const config = this.getLayoutConfig();
    const viewportWidth = this.slideWrapper.clientWidth || window.innerWidth;
    const activeImageRatio = 66.6666666667;
    const activeImageHeight = viewportWidth * (config.active / 100) * (activeImageRatio / 100);

    this.style.setProperty('--ct-carousel-active-width', `${config.active}%`);

    this.dragWrapper.style.height = `${activeImageHeight}px`;

    this.slideLayouts = [];

    this.slides.forEach((slide, index) => {
      const relative = this.getWrappedRelative(index - this.position);
      const layout = this.getLayoutForRelative(relative, config);
      const visualState = this.getSlideVisualState(relative);

      const isVisualActive = visualState.absRelative < 0.5;
      const isFar = visualState.absRelative > 1.5;
      const isNear = !isVisualActive && !isFar;

      this.slideLayouts[index] = {
        index,
        relative,
        left: layout.left,
        width: layout.width,
        center: layout.left + layout.width / 2
      };

      slide.style.position = 'absolute';
      slide.style.left = `${layout.left.toFixed(4)}%`;
      slide.style.width = `${layout.width.toFixed(4)}%`;
      slide.style.transformOrigin = '50% 0%';
      slide.style.transform = 'scale(1.001, 1.001)';
      slide.style.zIndex = isVisualActive ? '2' : '1';

      slide.style.setProperty('--ct-slide-ratio', `${visualState.imageRatio.toFixed(4)}%`);
      slide.style.setProperty('--ct-image-blur', `${visualState.imageBlur.toFixed(3)}px`);
      slide.style.setProperty('--ct-image-scale', visualState.imageScale.toFixed(4));
      slide.style.setProperty('--ct-image-brightness', visualState.imageBrightness.toFixed(4));
      slide.style.setProperty('--ct-image-saturation', visualState.imageSaturation.toFixed(4));

      slide.classList.toggle('is-active', isVisualActive);
      slide.classList.toggle('is-near', isNear);
      slide.classList.toggle('is-far', isFar);
    });

    const activeSlide = this.slides.find((slide) => slide.classList.contains('is-active'));
    const activeActions = activeSlide ? activeSlide.querySelector('[data-slide-actions]') : null;
    let visualHeight = activeImageHeight;

    if (activeActions) {
      visualHeight += activeActions.offsetHeight;
    }

    this.visual.style.height = `${Math.ceil(visualHeight)}px`;
    this.slideWrapper.style.height = `${Math.ceil(visualHeight)}px`;
  }

  getWrappedRelative(value) {
    const total = this.slides.length;

    if (total <= 1) {
      return 0;
    }

    const half = total / 2;
    let relative = value;

    while (relative > half) {
      relative -= total;
    }

    while (relative < -half) {
      relative += total;
    }

    return relative;
  }

  getSlideIndexFromPoint(clientX) {
    const rect = this.dragWrapper.getBoundingClientRect();
    const xPercent = ((clientX - rect.left) / rect.width) * 100;

    let matchedIndex = null;
    let matchedDistance = Infinity;

    this.slideLayouts.forEach((layout) => {
      const start = layout.left;
      const end = layout.left + layout.width;

      if (xPercent >= start && xPercent <= end) {
        const distance = Math.abs(layout.relative);

        if (distance < matchedDistance) {
          matchedDistance = distance;
          matchedIndex = layout.index;
        }
      }
    });

    if (matchedIndex !== null) {
      return matchedIndex;
    }

    this.slideLayouts.forEach((layout) => {
      const distance = Math.abs(layout.center - xPercent);

      if (distance < matchedDistance) {
        matchedDistance = distance;
        matchedIndex = layout.index;
      }
    });

    return matchedIndex;
  }

  snapAfterDrag(dragDistance) {
    const movement = Math.abs(this.position - this.dragStartPosition);
    let targetPosition = Math.round(this.position);

    if (Math.abs(dragDistance) > this.swipeThreshold && movement < 0.5) {
      targetPosition = this.dragStartPosition + (dragDistance < 0 ? 1 : -1);
    }

    this.animateTo(targetPosition);
  }

  goToSlide(index) {
    if (!this.slides[index]) {
      return;
    }

    const relative = this.getWrappedRelative(index - this.position);
    const targetPosition = this.position + relative;

    this.animateTo(targetPosition);
  }

  nextSlide() {
    this.animateTo(this.position + 1);
  }

  animateTo(targetPosition) {
    this.cancelAnimation();

    const startPosition = this.position;
    const distance = targetPosition - startPosition;

    if (Math.abs(distance) < 0.001 || this.prefersReducedMotion) {
      this.position = targetPosition;
      this.render();
      this.setActiveIndex(this.normaliseIndex(Math.round(this.position)), true);
      this.updateTextWrapperHeight();
      return;
    }

    const startTime = performance.now();

    const tick = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / this.animationDuration, 1);
      const easedProgress = this.easeOutCubic(progress);

      this.position = startPosition + distance * easedProgress;
      this.render();

      if (progress < 1) {
        this.animationFrame = window.requestAnimationFrame(tick);
      } else {
        this.position = targetPosition;
        this.render();
        this.setActiveIndex(this.normaliseIndex(Math.round(this.position)), true);
        this.updateTextWrapperHeight();
        this.animationFrame = null;
      }
    };

    this.animationFrame = window.requestAnimationFrame(tick);
  }

  setActiveIndex(index, updatePanels = true) {
    this.activeIndex = this.normaliseIndex(index);

    if (updatePanels) {
      this.textPanels.forEach((panel, panelIndex) => {
        panel.classList.toggle('active', panelIndex === this.activeIndex);
      });
    }

    this.updateTextWrapperHeight();
  }

  updateTextWrapperHeight() {
    if (!this.textWrapper || !this.textPanels || this.textPanels.length === 0) {
      return;
    }

    window.requestAnimationFrame(() => {
      const activePanel = this.textPanels[this.activeIndex];

      if (!activePanel) {
        return;
      }

      const activeContent = activePanel.querySelector('.ct-snohetta-carousel__text-column') || activePanel;
      const activeHeight = Math.ceil(activeContent.getBoundingClientRect().height);

      if (activeHeight > 0) {
        this.textWrapper.style.height = `${activeHeight}px`;
      }
    });
  }

  normaliseIndex(index) {
    const total = this.slides.length;

    if (total === 0) {
      return 0;
    }

    return ((index % total) + total) % total;
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.prefersReducedMotion || this.slides.length < 2) {
      return;
    }

    this.stopAutoplay();

    this.autoplayTimer = window.setInterval(() => {
      if (this.shouldAutoplayRun()) {
        this.nextSlide();
      }
    }, this.autoplayInterval);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  shouldAutoplayRun() {
    return !this.isHovering && !this.isFocused && !this.isDragging;
  }

  cancelAnimation() {
    if (this.animationFrame) {
      window.cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  easeOutCubic(progress) {
    return 1 - Math.pow(1 - progress, 3);
  }
}

if (!customElements.get('ct-snohetta-carousel')) {
  customElements.define('ct-snohetta-carousel', CTSnohettaCarousel);
}